import { useState } from "react";
import { common_utils } from "../../../../../../common/common_utils";
import { trpc } from "../../../utils/trpc";
import { DashboardUtils } from "../../../dashboard_utils";
import {
	LicenseType,
	LicenseTypeSelectionModal,
} from "./license_type_selection_modal";

interface ActivateButtonWithSelectionProps {
	is_premium: boolean;
	has_sesh_pro?: boolean;
	is_saving: boolean;
	is_admin: boolean;
	user_id: string;
	premium_activator_id: string;
	sesh_pro_activator_id?: string;
	guild_id: string;
	guild_name: string;
	onActivatePremium: () => Promise<boolean>;
	onDeactivatePremium: () => void;
	onActivatePro: () => Promise<boolean>;
	onDeactivatePro?: () => void;
}

export function ActivateButtonWithSelection(
	props: ActivateButtonWithSelectionProps
) {
	const [showSelectionModal, setShowSelectionModal] = useState(false);
	const [isActivating, setIsActivating] = useState(false);

	// Fetch premium details to know available slots
	const premiumDetailsQuery = trpc.premium.getUserPremiumDetails.useQuery();

	const premiumDetails = premiumDetailsQuery.data;

	// Calculate available slots
	const premiumAvailable = premiumDetails?.max_activated_servers ?? 0;
	const premiumUsed =
		premiumDetails?.transferrable_activated_servers_count ?? 0;
	const proAvailable = premiumDetails?.max_sesh_pro_activations ?? 0;
	const proUsed = premiumDetails?.sesh_pro_activated_servers_count ?? 0;

	const premiumRemaining = premiumAvailable - premiumUsed;
	const proRemaining = proAvailable - proUsed;

	const hasBothOptions = premiumRemaining > 0 && proRemaining > 0;
	const hasOnlyPremium = premiumRemaining > 0 && proRemaining <= 0;
	const hasOnlyPro = premiumRemaining <= 0 && proRemaining > 0;
	const hasNoOptions = premiumRemaining <= 0 && proRemaining <= 0;

	const button_class_names: string[] = [
		"button is-size-4-tablet is-size-5-mobile",
	];

	if (props.is_saving || isActivating) {
		button_class_names.push("is-loading");
	}

	// Determine if showing activate or deactivate
	const isCurrentlyActivated = props.is_premium || props.has_sesh_pro;

	if (isCurrentlyActivated) {
		button_class_names.push("dashboard-warning-button");
	} else {
		button_class_names.push("dashboard-premium-button");
	}

	// Determine if user can deactivate (must be admin or the activator)
	const canDeactivatePremium =
		props.is_premium &&
		(props.is_admin || props.premium_activator_id === props.user_id);
	const canDeactivatePro =
		props.has_sesh_pro &&
		(props.is_admin || props.sesh_pro_activator_id === props.user_id);

	let is_disabled = false;
	if (isCurrentlyActivated) {
		// For deactivation, check if user has permission
		if (props.has_sesh_pro) {
			is_disabled = !canDeactivatePro;
		} else {
			is_disabled = !canDeactivatePremium;
		}
	} else {
		// For activation, check if any slots available
		is_disabled = hasNoOptions && !premiumDetailsQuery.isLoading;
	}

	if (is_disabled) {
		button_class_names.push("input-disabled");
	}

	const handleActivation = async (type: LicenseType) => {
		setIsActivating(true);
		setShowSelectionModal(false);

		let success = false;
		if (type === "premium") {
			success = await props.onActivatePremium();
		} else {
			success = await props.onActivatePro();
		}

		setIsActivating(false);
		return success;
	};

	const handleClick = () => {
		if (isCurrentlyActivated) {
			// Deactivation flow
			const isDeactivatingPro = props.has_sesh_pro;
			const deactivateType = isDeactivatingPro ? "sesh Pro" : "Premium";

			DashboardUtils.ShowDefaultWarningAlert(
				<>
					Are you sure you want to remove {deactivateType} features
					from{" "}
					<span className="has-text-weight-bold">
						{props.guild_name}
					</span>
					?
					<br />
					<br />
					<div className="mx-auto w-fit rounded border border-base-light bg-base-semidark py-8 pl-4 pr-8">
						<div className="flex h-full items-start gap-3">
							<div className="flex">
								<i className="fas fa-info-circle fa-lg my-auto text-sesh-primary"></i>
							</div>
							<div className="content w-full text-center">
								<p className="mb-2">
									This will only deactivate {deactivateType}{" "}
									on this server, it will{" "}
									<span className="has-text-weight-bold has-text-danger">
										not
									</span>{" "}
									cancel your subscription.
								</p>
								<p className="mb-0">
									To modify your subscription, go{" "}
									<a
										className="manual-link has-text-weight-bold"
										href={common_utils.getPremiumPageURL()}
										target="_blank"
									>
										here
									</a>
									.
								</p>
							</div>
						</div>
					</div>
				</>
			).then((val) => {
				if (!val.isConfirmed) return;

				if (isDeactivatingPro && props.onDeactivatePro) {
					props.onDeactivatePro();
				} else {
					props.onDeactivatePremium();
				}
			});
		} else {
			// Activation flow
			if (hasBothOptions) {
				// Show selection modal
				setShowSelectionModal(true);
			} else if (hasOnlyPremium) {
				// Directly activate premium
				handleActivation("premium");
			} else if (hasOnlyPro) {
				// Directly activate pro
				handleActivation("pro");
			}
		}
	};

	// Determine button text
	let buttonText = "Activate";
	if (isCurrentlyActivated) {
		buttonText = props.has_sesh_pro
			? "Deactivate sesh Pro"
			: "Deactivate Premium";
	} else if (premiumDetailsQuery.isLoading) {
		buttonText = "Loading...";
	} else if (hasNoOptions) {
		buttonText = "No Activations Available";
	}

	return (
		<>
			<button
				id="premium-activate-button"
				className={button_class_names.join(" ")}
				type="submit"
				onClick={handleClick}
				disabled={
					props.is_saving ||
					isActivating ||
					is_disabled ||
					premiumDetailsQuery.isLoading
				}
			>
				<span className="has-text-weight-bold">{buttonText}</span>
			</button>

			<LicenseTypeSelectionModal
				open={showSelectionModal}
				onClose={() => setShowSelectionModal(false)}
				onSelect={handleActivation}
				premiumAvailable={premiumAvailable}
				premiumUsed={premiumUsed}
				proAvailable={proAvailable}
				proUsed={proUsed}
				guildName={props.guild_name}
			/>
		</>
	);
}
